import React, { useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import { setServerGamerule } from "src/actions/mine_craft/serversActions";
import { useAppDispatch } from "src/store/hooks";

import { AutoComplete, InputNumber, Input, Modal, Segmented, Switch, message } from 'antd';

// The rules players ask about most, offered as suggestions — the field
// stays free-text because Bedrock keeps adding rules.
const COMMON_RULES = [
  'keepInventory', 'doDaylightCycle', 'doWeatherCycle', 'doMobSpawning',
  'mobGriefing', 'pvp', 'showCoordinates', 'doFireTick', 'doInsomnia',
  'naturalRegeneration', 'randomTickSpeed', 'spawnRadius'
];

const RULE_REGEX = /^[A-Za-z]{1,40}$/;
const WORD_REGEX = /^[A-Za-z0-9]{1,20}$/;

type ValueKind = 'boolean' | 'number' | 'text';

// One rule at a time, applied live on the running server — the modal
// counterpart of typing `gamerule ...` into the console, with the value
// typed properly instead of stringly.
const _GamerulesForm = ({ server, open, onClose }: {
  server: ServerInterface,
  open: boolean,
  onClose: () => void
}) => {
  const dispatch = useAppDispatch();

  const [rule, setRule] = useState('');
  const [kind, setKind] = useState<ValueKind>('boolean');
  const [boolValue, setBoolValue] = useState(true);
  const [numberValue, setNumberValue] = useState<number | null>(null);
  const [textValue, setTextValue] = useState('');
  const [busy, setBusy] = useState(false);

  const value: boolean | number | string | null =
    kind === 'boolean' ? boolValue
    : kind === 'number' ? numberValue
    : textValue.trim();

  const valid = RULE_REGEX.test(rule.trim()) &&
    (kind === 'boolean' ||
     (kind === 'number' && numberValue !== null) ||
     (kind === 'text' && WORD_REGEX.test(textValue.trim())));

  async function apply() {
    if (!valid || value === null) return;

    setBusy(true);
    const resultAction = await dispatch(setServerGamerule({ id: server.id, rule: rule.trim(), value }));
    setBusy(false);

    if (setServerGamerule.fulfilled.match(resultAction)) {
      message.success(`gamerule ${rule.trim()} ${String(value)} sent`);
      onClose();
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'Gamerule failed — is the server running?');
    }
  }

  return (
    <Modal
      title='Set a gamerule'
      open={open}
      onCancel={onClose}
      onOk={apply}
      okText='Apply'
      okButtonProps={{ disabled: !valid, loading: busy }}
      destroyOnClose
    >
      <div className='v-srv-gamerule'>
        <div className='o-field'>
          <label>Rule</label>
          <AutoComplete
            value={rule}
            onChange={setRule}
            options={COMMON_RULES.map(name => ({ value: name }))}
            filterOption={(input, option) =>
              String(option?.value).toLowerCase().includes(input.toLowerCase())}
            placeholder='keepInventory'
          />
        </div>

        <div className='o-field'>
          <label>Value</label>
          <div className='o-value-row'>
            <Segmented
              size='small'
              value={kind}
              onChange={next => setKind(next as ValueKind)}
              options={[
                { value: 'boolean', label: 'true/false' },
                { value: 'number',  label: 'number' },
                { value: 'text',    label: 'word' }
              ]}
            />

            {kind === 'boolean' &&
              <Switch
                checked={boolValue}
                onChange={setBoolValue}
                checkedChildren='true'
                unCheckedChildren='false'
              />}

            {kind === 'number' &&
              <InputNumber
                value={numberValue}
                onChange={setNumberValue}
                placeholder='3'
              />}

            {kind === 'text' &&
              <Input
                className='o-word'
                value={textValue}
                maxLength={20}
                onChange={e => setTextValue(e.target.value)}
                placeholder='adventure'
              />}
          </div>
        </div>

        <div className='o-hint'>
          Runs <code>gamerule &lt;rule&gt; &lt;value&gt;</code> in the console — applies
          immediately, no restart, but the server must be running. For a stopped
          server edit the world settings instead.
        </div>
      </div>
    </Modal>
  );
}

export default _GamerulesForm;
